// Shared, deterministic framework routing. Identical file is shipped to the frontend.
import definitions from "./care-frameworks.json";
import english from "./cqc-profile-catalogue.json";
const PROFILE_VERSION = "uk-care-service-plan-v2";
function countryOf(site) {
  return site?.address?.country || site?.guidanceCountry || "";
}
function addressReady(site) {
  const a = site?.address;
  return !!(
    a?.line1 &&
    a?.town &&
    a?.postcode &&
    definitions.countries.includes(a?.country) &&
    a?.confirmedAt
  );
}
function selectionFor(profile = {}, country = "") {
  const serviceTypes = Array.isArray(profile.serviceTypes)
      ? profile.serviceTypes
      : [],
    careSetting = profile.careSetting || "unknown",
    issues = [];
  if (!definitions.countries.includes(country))
    return {
      frameworks: [],
      frameworkId: "",
      issues: ["Confirm the organisation address and UK country first."],
    };
  if (!serviceTypes.length)
    return {
      frameworks: [],
      frameworkId: "",
      issues: ["Select the services provided at this location."],
    };
  if (country === "England")
    return {
      frameworks: [
        {
          id: english.frameworkId,
          country,
          regulator: "CQC",
          label: english.frameworkLabel,
          source: english.sources[0].url,
          groups: english.groups.map((g) => ({
            ...g,
            items: g.items.map((i) => ({
              ...i,
              elements: english.categories.map((id) => ({ id })),
            })),
          })),
        },
      ],
      frameworkId: english.frameworkId,
      issues: [],
    };
  const frameworks = definitions.frameworks.filter(
    (f) =>
      !(
        f.id === "sc-his-ihc" &&
        careSetting === "mixed" &&
        serviceTypes.every((t) => t === "dental")
      ) &&
      f.country === country &&
      f.serviceTypes.some((s) => serviceTypes.includes(s)) &&
      (!f.settings.length || f.settings.includes(careSetting)),
  );
  for (const type of serviceTypes) {
    if (!frameworks.some((f) => f.serviceTypes.includes(type)))
      issues.push(
        `Confirm the regulatory route for ${english.services.find((s) => s.id === type)?.label || type} in ${country}; no matching framework is active for this service/setting.`,
      );
  }
  if (
    country === "Scotland" &&
    profile.functions?.children === "yes" &&
    serviceTypes.some((t) =>
      ["care-home", "nursing-home", "homecare", "supported-living"].includes(t),
    )
  )
    issues.push(
      "Children’s care needs its own Scottish service framework; this adult-care mapping must not be used as its substitute.",
    );
  return {
    frameworks,
    frameworkId: frameworks
      .map((f) => f.id)
      .sort()
      .join("+"),
    issues,
  };
}
function groupsForProfile(profile, country) {
  return selectionFor(profile, country)
    .frameworks.flatMap((f) =>
      f.groups.map((g) => ({
        ...g,
        country,
        frameworkId: f.id,
        frameworkLabel: f.label,
        regulator: f.regulator,
        source: f.source,
        items: g.items
          .filter(
            (i) =>
              (!i.serviceTypesAny ||
                i.serviceTypesAny.some((t) =>
                  profile.serviceTypes.includes(t),
                )) &&
              (!i.functionsAny ||
                i.functionsAny.some((k) => profile.functions?.[k] !== "no")),
          )
          .map((i) => ({
            ...i,
            elements: i.elements || [
              {
                id: "evidence",
                label: "Evidence and review",
                help: i.guidance,
              },
            ],
            source: f.source,
          })),
      })),
    )
    .filter((g) => g.items.length);
}
function keysForProfile(profile, country) {
  return groupsForProfile(profile, country).flatMap((g) =>
    g.items.flatMap((i) => i.elements.map((e) => `${g.id}|${i.id}|${e.id}`)),
  );
}
function profileReady(site) {
  const p = site?.serviceProfile;
  if (!p?.confirmedAt || !addressReady(site)) return false;
  const selection = selectionFor(p, countryOf(site));
  const legacy =
    countryOf(site) === "England" && p.catalogueVersion === english.version;
  return (
    !selection.issues.length &&
    p.jurisdiction === countryOf(site) &&
    p.frameworkId === selection.frameworkId &&
    (legacy ||
      (p.catalogueVersion === PROFILE_VERSION &&
        p.frameworkCatalogueVersion === definitions.version &&
        p.addressRevision === (site.addressRevision || 0)))
  );
}
export default {
  definitions,
  PROFILE_VERSION,
  countryOf,
  addressReady,
  selectionFor,
  groupsForProfile,
  keysForProfile,
  profileReady,
};
